// Card de exibição do desconto na assinatura — Story 3.8.
// Mostra preço base / desconto / efetivo + botões editar/remover OU aplicar.

import { Percent, Plus, Tag, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  calculateDiscountAmount,
  calculateEffectivePrice,
  formatBR,
  parseLocalDate,
} from '@control-center/shared';

import { PillButton } from '../clients/PillButton';
import { useRemoveDiscount } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';
import type { SubscriptionDetail } from '../../lib/subscriptions-api';
import { DiscountForm } from './DiscountForm';

interface DiscountDisplayProps {
  subscription: SubscriptionDetail;
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function DiscountDisplay({ subscription }: DiscountDisplayProps): JSX.Element {
  const [formOpen, setFormOpen] = useState(false);
  const removeMutation = useRemoveDiscount();

  const basePrice = Number(subscription.price ?? '0');
  const hasDiscount = Boolean(subscription.discountAmount && subscription.discountType);

  const discountInput = {
    price: basePrice,
    discountAmount: subscription.discountAmount ? Number(subscription.discountAmount) : null,
    discountType: subscription.discountType ?? null,
    discountUntil: subscription.discountUntil ?? null,
    discountMax: subscription.discountMax ? Number(subscription.discountMax) : null,
  };

  const effectivePrice = calculateEffectivePrice(discountInput);
  const discountValue = calculateDiscountAmount(discountInput);

  // Detecta se desconto expirou (mostra aviso visual)
  const expired = Boolean(
    subscription.discountUntil &&
    subscription.discountUntil < new Date().toISOString().slice(0, 10),
  );

  async function handleRemove(): Promise<void> {
    const ok = window.confirm('Remover o desconto? Próxima cobrança vai usar o preço cheio.');
    if (!ok) return;
    try {
      await removeMutation.mutateAsync(subscription.id);
      toast.success('Desconto removido.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao remover desconto.'));
    }
  }

  return (
    <>
      <section className="rounded-[14px] border border-line bg-panel p-5">
        <div className="mb-3 flex items-center gap-2">
          <Tag size={14} strokeWidth={1.8} className="text-muted" />
          <h3 className="m-0 text-[12px] font-semibold uppercase tracking-wider text-muted-3">
            Desconto manual
          </h3>
        </div>

        {!hasDiscount ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-[12px] text-muted">
              Nenhum desconto aplicado. Preço cheio: <strong>{formatBRL(basePrice)}</strong>/mês.
            </p>
            <PillButton
              type="button"
              onClick={() => setFormOpen(true)}
              icon={<Plus size={12} strokeWidth={2} />}
            >
              Aplicar desconto
            </PillButton>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <dl className="grid grid-cols-3 gap-3">
              <div>
                <dt className="text-[11px] uppercase tracking-wider text-muted-3">Preço base</dt>
                <dd className="mt-1 text-[15px] text-ink tnum">{formatBRL(basePrice)}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wider text-muted-3">Desconto</dt>
                <dd className="mt-1 text-[15px] text-bad tnum">
                  −{formatBRL(discountValue)}
                  {subscription.discountType === 'percent' && (
                    <span className="ml-1 text-[11px] text-muted">
                      ({subscription.discountAmount}%
                      {subscription.discountMax
                        ? `, até ${formatBRL(Number(subscription.discountMax))}`
                        : ''}
                      )
                    </span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wider text-muted-3">Preço efetivo</dt>
                <dd className="mt-1 text-[15px] font-semibold text-good tnum">
                  {formatBRL(effectivePrice)}
                </dd>
              </div>
            </dl>

            <div className="flex items-center justify-between gap-2 border-t border-line pt-3">
              <div className="flex flex-col gap-0.5 text-[11px] text-muted">
                {subscription.discountUntil ? (
                  <span>
                    Vigente até{' '}
                    <strong>{formatBR(parseLocalDate(subscription.discountUntil))}</strong>
                    {expired && (
                      <span className="ml-2 text-bad">(expirado — preço cheio na próxima)</span>
                    )}
                  </span>
                ) : (
                  <span>Desconto perpétuo</span>
                )}
                {subscription.discountReason && (
                  <span className="text-muted-2">Motivo: {subscription.discountReason}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <PillButton
                  type="button"
                  onClick={() => setFormOpen(true)}
                  icon={<Percent size={12} strokeWidth={2} />}
                >
                  Editar
                </PillButton>
                <PillButton
                  type="button"
                  onClick={() => void handleRemove()}
                  icon={<Trash2 size={12} strokeWidth={2} />}
                  disabled={removeMutation.isPending}
                >
                  {removeMutation.isPending ? 'Removendo…' : 'Remover'}
                </PillButton>
              </div>
            </div>
          </div>
        )}
      </section>

      <DiscountForm subscription={subscription} open={formOpen} onOpenChange={setFormOpen} />
    </>
  );
}
